import {
  Box,
  chakra,
  Checkbox,
  keyframes,
  Spinner,
  Stack,
  Tag,
  TagLabel,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useRequestGetDataToRegistration } from '../../../../services/hooks/CandidateArea/useGetDataToRegistration';
import Header from '../../../../components/Header/Header';
import CardHeaderMobile from '../../../../components/Mobile/header';
import CandidateInfo from '../../../../components/Mobile/cardUserInformation';
import RegistrationVacancyMobilePage from './VacancyForms';
import HeaderSectionMobilePage from '../../../../components/Mobile/headerSection';
import AlertMobile from '../../../../components/Mobile/alert';
import NavigationButtonMobilePage from '../../../../components/Mobile/navigationButton';
import { FieldErrors, FieldValues, useFormContext } from 'react-hook-form';
import RegistrationSpecialConditionMobilePage from './specialCondition';
import { UseRegistration } from '../../../../Store/useRegistration';
import RegistrationProtocolsMobilePage from './protocols';
import { IoMdAdd } from 'react-icons/io';
import { MdSystemUpdate } from 'react-icons/md';
import FormCreateExemptionMobile from '../../Exemptions/mobile/create/Form';
import ExistingExemptionAttachments from '../../Exemptions/Forms/ExistingExemptionAttachments';
import { isValidUUID } from '../../../../utils/functions';
import MessageError from '../../../../components/MessageError';
import { IResponseIdName } from '../../../../services/hooks/globalQuery';
import { getExemptionType, isEconomicExemption } from '../../../../services/hooks/Exemptions/economicHyposufficiency';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

export enum ERegistrationType {
  ONLY_REGISTRATION = 1,
  ONLY_EXEMPTION = 2,
  REGISTRATION_AND_EXEMPTION = 3,
}

interface IOwnProps {
  onSubmitForm(data: FieldValues): void;
  onInvalidForm?: (errors: FieldErrors) => void;
  isLoading: boolean;
  registrationType?: ERegistrationType;
  sendingForm: boolean;
  modalityAvailable?: IResponseIdName[];
}

interface IHookForms {
  type_exemption?: string;
  vacancy?: string;
  terms: boolean;
  is_declaration_veracity: boolean;
  eligibility_declaration: boolean;
  sub_type_of_competition?: IResponseIdName[];
  modality_type: IResponseIdName[];
}

const CreateRegistrationMobilePage: React.FC<IOwnProps> = ({
  onSubmitForm,
  onInvalidForm,
  registrationType,
  sendingForm,
  modalityAvailable,
}) => {
  const navigate = useNavigate();
  const { selectiveProcessId, registrationId } = UseRegistration();

  const { register, handleSubmit, formState, watch } = useFormContext<IHookForms>();
  const isEconomic = isEconomicExemption(getExemptionType(watch('type_exemption'))?.name);

  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const selectiveProcessData = useRequestGetDataToRegistration(selectiveProcessId || '');

  const loading = selectiveProcessData.isLoading;

  const handleMountObj = (data: FieldValues) => {
    if (data?.modality_type && data.modality_type.length > 0) {
      data.modality_type = data.modality_type
        .filter((item: string | boolean) => item)
        .map((item: string) => {
          if (item) {
            const typeOfCompetition = modalityAvailable?.find((modality: IResponseIdName) => modality.id === item);
            return JSON.stringify(typeOfCompetition);
          }
        });
    }

    onSubmitForm(data);
  };

  let textSendButton = '--';

  if (registrationId) {
    textSendButton = 'Atualizar';
  } else if (registrationType !== ERegistrationType.ONLY_REGISTRATION) {
    textSendButton = 'Solicitar isenção';
  } else {
    textSendButton = 'Inscrever-se';
  }

  let instructionForCandidate = `Nesta tela, você poderá realizar sua inscrição selecionando a ${selectiveProcessData.data?.is_cultural_house ? 'turma desejada' : 'vaga/cargo desejado e definindo a modalidade de preferência'}. Caso possua algum atendimento especial, será possível registrá-la. ${registrationType === ERegistrationType.REGISTRATION_AND_EXEMPTION ? 'Informe os dados para isenção e revise suas informações.' : ''} Antes de concluir, revise todos os dados preenchidos e finalize clicando em "${textSendButton}".`;

  if (registrationType === ERegistrationType.ONLY_EXEMPTION) {
    instructionForCandidate =
      `Confira o tipo de isenção e os dados, ajuste os anexos necessários e finalize clicando em "${textSendButton}".`;
  }

  const typeRegistrationText =
    registrationType === ERegistrationType.REGISTRATION_AND_EXEMPTION
      ? 'inscrição e isenção'
      : registrationType === ERegistrationType.ONLY_EXEMPTION ? 'isenção' : 'inscrição';

  const isUpdating = isValidUUID(registrationId);

  return (
    <>
      <Stack height="calc(100% - 35px)" w="100%" top={0} bg="white" gap={0}>
        <Header />
        <VStack
          mt={0}
          flex={1}
          overflowY="auto"
          py={2}
          gap={selectiveProcessData.data?.exemption_request_notices ? 2 : 0}
        >
          {(loading && (
            <VStack flex={1} w="100%" justify="center">
              <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
              <Text fontSize="sm">Buscando inscrições disponiveis...</Text>
            </VStack>
          )) || (
            <>
              {(selectiveProcessData.data?.exemption_request_notices ||
                selectiveProcessData?.data?.registration_confirmation) && (
                <AlertMobile title={`Informação para ${typeRegistrationText}`}>
                  {registrationType !== ERegistrationType.ONLY_REGISTRATION
                    ? selectiveProcessData.data.exemption_request_notices
                    : selectiveProcessData?.data?.registration_confirmation}
                </AlertMobile>
              )}
              <AnimatedBox
                bgGradient={bgGradient}
                borderRadius="lg"
                boxShadow="lg"
                mx={3}
                w="calc(100% - 24px)"
                minW={0}
                p={{ base: 5, md: 10 }}
                borderWidth={'1px'}
                borderColor={borderColor}
                bg="white"
                flex={1}
                display="flex"
                position="initial"
              >
                <VStack boxSizing="border-box" w="full" minW={0} maxW="full" spacing={4} align="stretch" pb={10}>
                  <CardHeaderMobile
                    title={`Formulario de ${typeRegistrationText}`}
                    gap={1}
                    px={3}
                    badgeIcon={isUpdating ? <MdSystemUpdate size={14} /> : <IoMdAdd size={14} />}
                    badgeText={isUpdating ? 'Editar' : 'Nova'}
                    colorScheme="green"
                    subTitle={instructionForCandidate}
                  />
                  <Stack as="form" w="full" minW={0} onSubmit={handleSubmit(handleMountObj, onInvalidForm)} noValidate id="form-registration">
                    <Tag size="sm" w="fit-content" variant="outline" colorScheme="purple">
                      <TagLabel>{selectiveProcessData.data?.selective_process_type_name || ''}</TagLabel>
                    </Tag>
                    <HeaderSectionMobilePage>{selectiveProcessData.data?.name}</HeaderSectionMobilePage>
                    <Stack gap={5}>
                      <CandidateInfo />
                      {registrationType !== ERegistrationType.ONLY_EXEMPTION && (
                        <>
                          <RegistrationVacancyMobilePage selectiveProcess={selectiveProcessData.data} />
                          {selectiveProcessData.data?.allows_special_condition && (
                            <RegistrationSpecialConditionMobilePage
                              specialCondition={selectiveProcessData.data.special_condition}
                            />
                          )}
                          {selectiveProcessData.data?.protocol && selectiveProcessData.data?.protocol.length > 0 && (
                            <RegistrationProtocolsMobilePage selectiveProcess={selectiveProcessData.data} />
                          )}
                        </>
                      )}
                      {registrationType !== ERegistrationType.ONLY_REGISTRATION && (
                        <>
                          <FormCreateExemptionMobile
                            selectiveProcessId={selectiveProcessId}
                            isUpdating={isUpdating}
                            requestNis={selectiveProcessData.data?.request_nis ?? false}
                            requestNisDocument={selectiveProcessData.data?.request_nis_document ?? false}
                            requestRedome={selectiveProcessData.data?.request_redome ?? false}
                          />
                          {isUpdating && !isEconomic && <ExistingExemptionAttachments />}
                        </>
                      )}
                      {!selectiveProcessData?.data?.display_terms_acceptance_before_registration &&
                        registrationType !== ERegistrationType.ONLY_EXEMPTION && (
                          <Stack mt={4} spacing={2}>
                            <Box maxH="150px" overflowY="auto" borderRadius="md" pt={2}>
                              <Checkbox isChecked={watch('terms')} {...register('terms')}>
                                <Text
                                  overflow="auto"
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      selectiveProcessData?.data?.terms_of_use ||
                                      'Declaro ter lido e concordado com os termos do edital.',
                                  }}
                                />
                              </Checkbox>
                              <MessageError>{formState.errors.terms?.message}</MessageError>
                            </Box>
                          </Stack>
                        )}
                      {selectiveProcessData?.data?.show_eligibility_declaration &&
                        registrationType !== ERegistrationType.ONLY_EXEMPTION && (
                          <Stack>
                            <Checkbox
                              isChecked={watch('eligibility_declaration')}
                              {...register('eligibility_declaration')}
                            >
                              <Text
                                overflow="auto"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    selectiveProcessData?.data?.text_eligibility_declaration || 'Declaração de titulos',
                                }}
                              />
                            </Checkbox>
                            <MessageError>{formState?.errors.eligibility_declaration?.message}</MessageError>
                          </Stack>
                        )}
                      {registrationType !== ERegistrationType.ONLY_REGISTRATION && (
                        <Stack>
                          <Checkbox
                            isChecked={watch('is_declaration_veracity')}
                            {...register('is_declaration_veracity')}
                            alignItems="flex-start"
                            mb={5}
                          >
                            <Text overflow="auto">
                              Declaração de veracidade das informações, conforme a Lei nº 13.656/2018, ciente das
                              penalidades por falsidade.
                            </Text>
                          </Checkbox>
                          <MessageError>{formState.errors.is_declaration_veracity?.message}</MessageError>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </VStack>
              </AnimatedBox>
            </>
          )}
        </VStack>
      </Stack>
      <NavigationButtonMobilePage
        absolute
        mainButtonText={textSendButton}
        goBackButtonText="Voltar"
        onGoBackButton={() => navigate('/painel/inscricoes')}
        formId="form-registration"
        typeButton="submit"
        isLoading={sendingForm}
      />
    </>
  );
};

export default CreateRegistrationMobilePage;
